import React, { useEffect, useState } from "react";
import { Package, Plus, Pencil, Search } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money, num, Spinner, PageHeader, Empty } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import { FieldW, SelectBox } from "@/pages/Clients";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const CATS = ["General", "Meat", "Dairy", "Bakery", "Sauce", "Grain", "Vegetable", "Oil", "Packaging"];
const EMPTY = {
  name: "", category: "General", purchase_unit: "kg", consumption_unit: "g", unit_conversion: 1000,
  minimum_stock: 0, reorder_threshold: 0, recommended_reorder_qty: 0, supplier: "", internal_cost: 0,
  markup_override_pct: "", shelf_life_days: "", expiry_date: "", active: true, current_quantity: 0,
};

function StockBadge({ i }) {
  if (i.out_of_stock) return <span className="text-xs font-semibold text-rose-400">OUT OF STOCK</span>;
  if (i.reorder_required) return <span className="text-xs font-semibold text-orange-400">REORDER</span>;
  if (i.low_stock) return <span className="text-xs font-semibold text-amber-400">LOW</span>;
  if (i.expiring_soon) return <span className="text-xs font-semibold text-rose-300">EXPIRING</span>;
  return <span className="text-xs text-emerald-400">OK</span>;
}

export default function Ingredients() {
  const { user } = useAuth();
  const isStaff = ["admin", "kitchen", "finance"].includes(user.role);
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [onlyPricing, setOnlyPricing] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [corr, setCorr] = useState({ q: "", reason: "" });

  const load = () => api.get("/ingredients").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (i) => {
    setForm({ ...EMPTY, ...i, markup_override_pct: i.markup_override_pct ?? "", shelf_life_days: i.shelf_life_days ?? "", expiry_date: i.expiry_date ?? "" });
    setCorr({ q: i.current_quantity, reason: "" });
    setEditId(i.id); setOpen(true);
  };

  const correctStock = async () => {
    try {
      await api.post("/inventory/correct", { ingredient_id: editId, new_quantity: Number(corr.q), reason: corr.reason });
      toast.success("Stock corrected"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        unit_conversion: Number(form.unit_conversion) || 1,
        minimum_stock: Number(form.minimum_stock), reorder_threshold: Number(form.reorder_threshold),
        recommended_reorder_qty: Number(form.recommended_reorder_qty), internal_cost: Number(form.internal_cost),
        markup_override_pct: form.markup_override_pct === "" ? null : Number(form.markup_override_pct),
        shelf_life_days: form.shelf_life_days === "" ? null : Number(form.shelf_life_days),
        expiry_date: form.expiry_date || null,
      };
      if (editId) await api.put(`/ingredients/${editId}`, payload);
      else await api.post("/ingredients", payload);
      toast.success(editId ? "Ingredient updated" : "Ingredient created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!list) return <Spinner />;
  const pricingCount = list.filter((i) => i.needs_pricing).length;
  const filtered = list
    .filter((i) => i.name.toLowerCase().includes(q.toLowerCase()) || i.category.toLowerCase().includes(q.toLowerCase()))
    .filter((i) => (onlyPricing ? i.needs_pricing : true));

  return (
    <div>
      <PageHeader title="Ingredient Master" subtitle="Central kitchen inventory · internal vs client-facing rates">
        {isStaff && (
          <Button onClick={openNew} data-testid="add-ingredient-btn" className="gap-1.5 bg-primary text-primary-foreground hover:bg-amber-400 font-semibold">
            <Plus className="h-4 w-4" /> New Ingredient
          </Button>
        )}
      </PageHeader>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative max-w-sm flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ingredients…" className="pl-9" data-testid="ingredient-search" />
        </div>
        {isStaff && (
          <Button variant={onlyPricing ? "default" : "outline"} onClick={() => setOnlyPricing((v) => !v)} data-testid="needs-pricing-filter"
            className={onlyPricing ? "bg-primary text-primary-foreground hover:bg-amber-400 font-semibold gap-1.5" : "gap-1.5"}>
            Needs pricing{pricingCount > 0 ? ` (${pricingCount})` : ""}
          </Button>
        )}
      </div>

      {filtered.length === 0 ? (
        <Empty icon={Package} title="No ingredients found" />
      ) : (
        <div className="card-surface overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border">
                <TableHead>Ingredient</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Stock</TableHead>
                {isStaff && <TableHead className="text-right">Internal</TableHead>}
                <TableHead className="text-right">Client Rate</TableHead>
                <TableHead className="text-right">Status</TableHead>
                {isStaff && <TableHead></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((i) => (
                <TableRow key={i.id} className="border-border" data-testid={`ingredient-row-${i.id}`}>
                  <TableCell className="font-medium text-white">
                    {i.name}
                    {i.needs_pricing && <span className="ml-2 text-[10px] font-bold text-amber-400 border border-amber-500/40 rounded px-1.5 py-0.5">NEEDS PRICING</span>}
                    <span className="block text-[11px] text-muted-foreground font-mono">{i.purchase_unit} → {i.consumption_unit} · ×{num(i.unit_conversion)}</span>
                  </TableCell>
                  <TableCell className="text-slate-300">{i.category}</TableCell>
                  <TableCell className="text-right font-mono text-slate-200">{num(i.current_quantity)} {i.purchase_unit}</TableCell>
                  {isStaff && <TableCell className="text-right font-mono text-slate-400">{money(i.internal_cost)}</TableCell>}
                  <TableCell className="text-right font-mono text-amber-300">{money(i.client_facing_rate)}</TableCell>
                  <TableCell className="text-right"><StockBadge i={i} /></TableCell>
                  {isStaff && (
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(i)} data-testid={`edit-ingredient-${i.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto bg-card border-border">
          <DialogHeader><DialogTitle>{editId ? "Edit Ingredient" : "New Ingredient"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <FieldW label="Name" full><Input data-testid="ingredient-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></FieldW>
            <FieldW label="Category"><SelectBox value={form.category} onChange={(v) => set("category", v)} options={CATS} testid="ingredient-category" /></FieldW>
            <FieldW label="Supplier"><Input value={form.supplier} onChange={(e) => set("supplier", e.target.value)} /></FieldW>
            <FieldW label="Purchase unit"><Input value={form.purchase_unit} onChange={(e) => set("purchase_unit", e.target.value)} placeholder="kg" /></FieldW>
            <FieldW label="Consumption unit"><Input value={form.consumption_unit} onChange={(e) => set("consumption_unit", e.target.value)} placeholder="g" /></FieldW>
            <FieldW label="Unit conversion (consumption per purchase)"><Input type="number" value={form.unit_conversion} onChange={(e) => set("unit_conversion", e.target.value)} /></FieldW>
            <FieldW label="Internal cost (AED / purchase unit)"><Input type="number" data-testid="ingredient-cost" value={form.internal_cost} onChange={(e) => set("internal_cost", e.target.value)} /></FieldW>
            <FieldW label="Minimum stock"><Input type="number" value={form.minimum_stock} onChange={(e) => set("minimum_stock", e.target.value)} /></FieldW>
            <FieldW label="Reorder threshold"><Input type="number" value={form.reorder_threshold} onChange={(e) => set("reorder_threshold", e.target.value)} /></FieldW>
            <FieldW label="Recommended reorder qty"><Input type="number" value={form.recommended_reorder_qty} onChange={(e) => set("recommended_reorder_qty", e.target.value)} /></FieldW>
            <FieldW label="Markup override %"><Input type="number" value={form.markup_override_pct} onChange={(e) => set("markup_override_pct", e.target.value)} placeholder="Use default" /></FieldW>
            <FieldW label="Expiry date"><Input type="date" value={form.expiry_date} onChange={(e) => set("expiry_date", e.target.value)} /></FieldW>
            <FieldW label="Shelf life (days)"><Input type="number" value={form.shelf_life_days} onChange={(e) => set("shelf_life_days", e.target.value)} /></FieldW>
          </div>
          {editId && user.role === "admin" && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
              <p className="text-xs font-semibold text-amber-300 mb-2">Super Admin — correct stock quantity</p>
              <div className="flex items-end gap-2">
                <div className="w-32"><label className="text-[11px] text-muted-foreground">New qty ({form.purchase_unit})</label><Input type="number" value={corr.q} onChange={(e) => setCorr({ ...corr, q: e.target.value })} data-testid="correct-qty" /></div>
                <div className="flex-1"><label className="text-[11px] text-muted-foreground">Reason</label><Input value={corr.reason} onChange={(e) => setCorr({ ...corr, reason: e.target.value })} data-testid="correct-reason" placeholder="Reason for correction" /></div>
                <Button variant="outline" onClick={correctStock} disabled={corr.reason.trim() === ""} data-testid="correct-stock-btn">Apply</Button>
              </div>
            </div>
          )}
          {editId && user.role !== "admin" && <p className="text-xs text-muted-foreground">Stock quantity is not editable here — use Purchases, Opening Stock or an Inventory Adjustment.</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy || !form.name} data-testid="save-ingredient-btn" className="bg-primary text-primary-foreground hover:bg-amber-400 font-semibold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
